/**
 * Global-filter popovers (mandate §6) — composed surface behind the header's two chips. Semantics live in
 * model/global-filters.ts; this renders them: five range presets that broadcast to every same-vocabulary
 * widget, and a searchable SINGLE-select symbol picker (every consumer takes one symbol — a multi-select the
 * consumers cannot honour would be a lying control). The active filter is always visible on the chip itself,
 * and both popovers carry the reset.
 */
import type { ViewModel } from '@/screens/generated/view-model';
import { css } from '@pointfive/ui';

const pop = css(
  'position:fixed;top:64px;left:24px;z-index:65;width:240px;border-radius:14px;border:1px solid var(--card-line);background:var(--surface);box-shadow:var(--shadow-float);padding:10px',
);
const item = (on: boolean) =>
  css(
    `display:flex;align-items:center;gap:8px;width:100%;text-align:left;font-size:11.5px;padding:8px 10px;border-radius:9px;border:none;cursor:pointer;background:${on ? 'var(--rail)' : 'transparent'};color:${on ? 'var(--rail-ink)' : 'var(--text-mid)'}`,
  );
const resetRow = css(
  'width:100%;text-align:center;font-size:10.5px;padding:7px;margin-top:6px;border-top:1px solid var(--l7);border-left:none;border-right:none;border-bottom:none;background:transparent;color:var(--muted);cursor:pointer',
);
const search = css(
  'width:100%;padding:7px 10px;margin-bottom:6px;border-radius:9px;border:1px solid var(--l10);background:var(--l2);color:var(--text);font-size:11.5px',
);

export function GlobalFilters({ m }: { m: Partial<ViewModel> }) {
  if (!m.gFilterOpen) return null;

  return (
    <>
      {/* click-away: a fixed underlay below the popover's z-index */}
      <div style={css('position:fixed;inset:0;z-index:64')} onClick={m.gFilterClose} aria-hidden="true" />
      <div
        style={pop}
        role="dialog"
        aria-label={m.gFilterOpen === 'range' ? 'Global date range' : 'Global symbol filter'}
      >
        {m.gFilterOpen === 'range' ? (
          <>
            {(m.gRangeChoices ?? []).map((r) => (
              <button key={r.id} style={item(r.on)} onClick={r.go} aria-pressed={r.on}>
                {r.label}
              </button>
            ))}
            <button style={resetRow} onClick={m.gFilterReset}>
              Reset all filters
            </button>
          </>
        ) : (
          <>
            <input
              style={search}
              placeholder="Search symbols…"
              aria-label="Search symbols"
              value={String(m.gFilterSymQuery ?? '')}
              onChange={(e) => m.gFilterSymSetQuery?.(e.target.value)}
            />
            <div style={css('max-height:240px;overflow-y:auto')}>
              {(m.gSymChoices ?? []).map((sym) => (
                <button key={sym.id} style={item(sym.on)} onClick={sym.go} aria-pressed={sym.on}>
                  {sym.label}
                </button>
              ))}
            </div>
            <button style={resetRow} onClick={m.gFilterReset}>
              Reset all filters
            </button>
          </>
        )}
      </div>
    </>
  );
}
